// The real backend (docs/backend-handover.md): one fetch per method. C-B1 built sign-in, users, settings, the logs,
// vendors, the Lender Hub, numbers, IVR numbers, templates, voice messages, sources, leads and suppression; C-ALL the
// audiences, workflows, campaigns and runs (part 3) and classification, lists, batches, groups (part 4). A method not
// built answers 501, so a screen that needs one fails loudly instead of showing empty data. main.tsx picks this API
// when VITE_API_URL is set
// (e.g. /api/campaign/v1); the mock stays the default. The session is a cookie; every change carries the CSRF token
// the backend put in the readable cookie ff_campaign_csrf. Errors arrive as { error } and become ApiError.
import { ApiError, type CampaignApi } from './CampaignApi'

const CSRF_COOKIE = 'ff_campaign_csrf'

function csrfToken(): string {
  const entry = document.cookie.split('; ').find((part) => part.startsWith(CSRF_COOKIE + '='))
  return entry ? decodeURIComponent(entry.slice(CSRF_COOKIE.length + 1)) : ''
}

/** ?a=1&b=2 from the defined values only (ids and enum words: never personal data in a URL). */
function query(values: Record<string, string | number | undefined> = {}): string {
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(values))
    if (value !== undefined && value !== '') params.set(key, String(value))
  const text = params.toString()
  return text ? `?${text}` : ''
}

const id = (value: string) => encodeURIComponent(value)

export function createHttpApi(baseUrl: string): CampaignApi {
  const base = baseUrl.replace(/\/$/, '')

  async function call<T>(method: string, path: string, body?: unknown): Promise<T> {
    const headers: Record<string, string> = { accept: 'application/json' }
    if (method !== 'GET') headers['x-csrf-token'] = csrfToken()
    if (body !== undefined) headers['content-type'] = 'application/json'
    let res: Response
    try {
      res = await fetch(base + path, {
        method,
        headers,
        credentials: 'same-origin',
        body: body === undefined ? undefined : JSON.stringify(body),
      })
    } catch {
      throw new ApiError(503, 'The portal could not reach its server. Check the connection and try again.')
    }
    if (res.status === 204) return undefined as T
    const text = await res.text()
    const data: unknown = text ? JSON.parse(text) : null
    if (!res.ok) {
      const message = (data as { error?: unknown } | null)?.error
      throw new ApiError(
        res.status,
        typeof message === 'string' ? message : `The server answered ${res.status}.`
      )
    }
    return data as T
  }

  const get = <T>(path: string) => call<T>('GET', path)
  const post = <T>(path: string, body: unknown = {}) => call<T>('POST', path, body)
  const put = <T>(path: string, body: unknown) => call<T>('PUT', path, body)
  const patch = <T>(path: string, body: unknown) => call<T>('PATCH', path, body)

  const built: Partial<CampaignApi> = {
    // sign-in and My account
    getSession: () => get('/session'),
    signIn: (input) => post('/session/sign-in', input),
    verifyTwoStep: (input) => post('/session/verify', input),
    changePassword: (input) => post('/session/password', input),
    setUpTwoStep: () => post('/session/2-step/setup'),
    confirmTwoStep: (input) => post('/session/2-step/confirm', input),
    renewRecoveryCodes: (input) => post('/session/2-step/recovery-codes', input),
    disableTwoStep: (input) => post('/session/2-step/disable', input),
    signOut: () => post('/session/sign-out'),
    // users, settings, clock, overview, logs
    listUsers: () => get('/users'),
    addUser: (input) => post('/users', input),
    updateUser: (userId, input) => patch(`/users/${id(userId)}`, input),
    resetPassword: (userId) => post(`/users/${id(userId)}/reset-password`),
    resetTwoStep: (userId) => post(`/users/${id(userId)}/reset-2-step`),
    getSettings: () => get('/settings'),
    saveSettings: (input) => put('/settings', input),
    getClock: () => get('/clock'),
    getOverview: () => get('/overview'),
    listEvents: () => get('/events'),
    listWebhooks: (campaignId) => get(`/webhooks${query({ campaign_id: campaignId })}`),
    listAudit: () => get('/audit'),
    // vendors, their APIs and changes, the Lender Hub
    listVendors: () => get('/vendors'),
    getVendor: (vendorId) => get(`/vendors/${id(vendorId)}`),
    addVendor: (input) => post('/vendors', input),
    onboardVendor: (vendorId, input) => post(`/vendors/${id(vendorId)}/onboard`, input),
    switchOffVendor: (vendorId) => post(`/vendors/${id(vendorId)}/switch-off`),
    getVendorApi: (apiId) => get(`/vendor-apis/${id(apiId)}`),
    switchOffApi: (apiId) => post(`/vendor-apis/${id(apiId)}/switch-off`),
    testVendorChange: (vendorId, change) => post(`/vendors/${id(vendorId)}/changes/test`, change),
    requestVendorChange: (vendorId, input) => post(`/vendors/${id(vendorId)}/changes`, input),
    decideVendorChange: (changeId, decision) =>
      post(`/vendor-changes/${id(changeId)}/decision`, { decision }),
    listVendorChanges: (filter = {}) => get(`/vendor-changes${query(filter)}`),
    listHubLenders: () => get('/hub/lenders'),
    getHubLender: (slug) => get(`/hub/lenders/${id(slug)}`),
    listHubRoutes: () => get('/hub/routes'),
    setRouteKind: (routeId, input) => put(`/hub/routes/${id(routeId)}/kind`, input),
    syncHub: () => post('/hub/sync'),
    // channels
    listNumbers: (filter = {}) => get(`/numbers${query(filter)}`),
    pauseNumber: (numberId) => post(`/numbers/${id(numberId)}/pause`),
    resumeNumber: (numberId) => post(`/numbers/${id(numberId)}/resume`),
    markNumberCleared: (numberId) => post(`/numbers/${id(numberId)}/cleared`),
    refreshNumberStatus: () => post('/numbers/refresh-status'),
    importWhatsAppNumbers: () => post('/numbers/whatsapp/import'),
    startWhatsAppNumber: (input) => post('/numbers/whatsapp', input),
    addWhatsAppNumber: (input) => post('/numbers/whatsapp/existing', input),
    checkWhatsAppNumber: (numberId) => post(`/numbers/${id(numberId)}/check-meta`),
    finishWhatsAppNumber: (numberId, input) => post(`/numbers/${id(numberId)}/finish-by-hand`, input),
    listWhatsAppAccounts: () => get('/whatsapp-accounts'),
    addWhatsAppAccount: (input) => post('/whatsapp-accounts', input),
    editWhatsAppAccount: (accountId, input) => put(`/whatsapp-accounts/${id(accountId)}`, input),
    setNumberAccount: (numberId, input) => put(`/numbers/${id(numberId)}/whatsapp-account`, input),
    listIvrVendors: () => get('/ivr/vendors'),
    syncIvrNumbers: (vendorId) => post(`/ivr/vendors/${id(vendorId)}/sync`),
    listNumbersForSale: (vendorId, filter) => post(`/ivr/vendors/${id(vendorId)}/for-sale`, filter),
    buyIvrNumber: (vendorId, input) => post(`/ivr/vendors/${id(vendorId)}/buy`, input),
    addIvrNumber: (input) => post('/ivr/numbers', input),
    listTemplates: (filter = {}) => get(`/templates${query(filter)}`),
    createTemplate: (input) => post('/templates', input),
    syncTemplates: () => post('/templates/sync'),
    setTemplateForCampaigns: (templateId, forCampaigns) =>
      post(`/templates/${id(templateId)}/${forCampaigns ? 'for-campaigns' : 'not-for-campaigns'}`),
    listVoiceMessages: () => get('/voice-messages'),
    createVoiceMessage: (input) => post('/voice-messages', input),
    newVoiceVersion: (fromId, input) => post(`/voice-messages/${id(fromId)}/versions`, input),
    decideVoiceMessage: (messageId, input) => post(`/voice-messages/${id(messageId)}/decision`, input),
    // data
    listSources: () => get('/sources'),
    listPartners: () => get('/partners'),
    fetchSourceNow: (sourceId) => post(`/sources/${id(sourceId)}/fetch`),
    listLeads: (filter) => post('/leads/search', filter),
    leadHistory: (input) => post('/leads/history', input),
    listSuppression: () => get('/suppression'),
    addSuppression: (input) => post('/suppression', input),
    importDnd: (input) => post('/suppression/dnd-import', input),
    getDndStatus: () => get('/suppression/dnd-status'),
    listDndImports: () => get('/suppression/dnd-imports'),
    removeSuppression: (rowId, input) => post(`/suppression/${id(rowId)}/remove`, input),
    // C-ALL part 3: the recording itself (raw bytes after the version is saved) and where Play finds it
    uploadVoiceFile: async (messageId, file) => {
      let res: Response
      try {
        res = await fetch(`${base}/voice-messages/${id(messageId)}/file`, {
          method: 'PUT',
          headers: { 'x-csrf-token': csrfToken(), 'content-type': file.type || 'audio/wav' },
          credentials: 'same-origin',
          body: file,
        })
      } catch {
        throw new ApiError(503, 'The portal could not reach its server. Check the connection and try again.')
      }
      if (!res.ok) {
        const data = (await res.json().catch(() => null)) as { error?: string } | null
        throw new ApiError(res.status, data?.error ?? `The server answered ${res.status}.`)
      }
    },
    voiceFileUrl: (message) =>
      message.source === 'recording' && message.file
        ? `${base}/voice-files/vm-${id(message.id)}.${message.file.format}`
        : null,
    // audiences, workflows, campaigns, runs (C-ALL part 3: the run engine)
    listAudiences: () => get('/audiences'),
    countAudienceNow: (input) => post('/audiences/count', input),
    getAudience: (audienceId) => get(`/audiences/${id(audienceId)}`),
    createAudience: (input) => post('/audiences', input),
    listWorkflows: () => get('/workflows'),
    getWorkflow: (workflowId) => get(`/workflows/${id(workflowId)}`),
    createWorkflow: (input) => post('/workflows', input),
    newWorkflowDraft: (workflowId) => post(`/workflows/${id(workflowId)}/drafts`),
    saveWorkflowDraft: (versionId, document) => put(`/versions/${id(versionId)}`, document),
    addWorkflowNode: (versionId, input) => post(`/versions/${id(versionId)}/nodes`, input),
    publishWorkflowVersion: (versionId) => post(`/versions/${id(versionId)}/publish`),
    setWorkflowState: (workflowId, input) => post(`/workflows/${id(workflowId)}/state`, input),
    deleteWorkflow: (workflowId) => post(`/workflows/${id(workflowId)}/delete`),
    discardWorkflowDraft: (versionId) => post(`/versions/${id(versionId)}/discard`),
    listCampaigns: () => get('/campaigns'),
    getCampaign: (campaignId) => get(`/campaigns/${id(campaignId)}`),
    createCampaign: (input) => post('/campaigns', input),
    approveCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/approve`),
    startCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/start`),
    pauseCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/pause`),
    resumeCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/resume`),
    stopCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/stop`),
    changeCampaignSchedule: (campaignId, schedule) => post(`/campaigns/${id(campaignId)}/schedule`, schedule),
    getRun: (runId) => get(`/runs/${id(runId)}`),
    // W5: the events in ClickHouse
    getCampaignDelivery: (campaignId) => get(`/campaigns/${id(campaignId)}/delivery`),
    getCampaignReport: (campaignId) => get(`/campaigns/${id(campaignId)}/report`),
    listCampaignRuns: (campaignId, query) => {
      const q = new URLSearchParams()
      for (const [key, value] of Object.entries(query)) if (value !== undefined && value !== '') q.set(key, String(value))
      const qs = q.toString()
      return get(`/campaigns/${id(campaignId)}/runs${qs ? `?${qs}` : ''}`)
    },
    // WH1: the campaign's webhook lines; the CSV is a plain link (the browser saves it as it streams)
    listCampaignWebhooks: (campaignId, filters) => get(`/campaigns/${id(campaignId)}/webhooks${query(filters)}`),
    campaignWebhooksCsvUrl: (campaignId, filters) =>
      `${base}/campaigns/${id(campaignId)}/webhooks.csv${query({ ...filters, before: undefined })}`,
    // the campaign wizard (W2)
    createCampaignDraft: (input) => post('/campaign-drafts', input),
    getCampaignDraft: (campaignId) => get(`/campaigns/${id(campaignId)}/draft`),
    saveCampaignDraft: (campaignId, input) => put(`/campaigns/${id(campaignId)}/draft`, input),
    submitCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/submit`),
    deleteCampaignDraft: (campaignId) => post(`/campaigns/${id(campaignId)}/delete-draft`),
    copyCampaign: (campaignId) => post(`/campaigns/${id(campaignId)}/copy`),
    countCampaignAudience: (input) => post('/campaign-audience/count', input),
    listRemovedPeople: (input) => post('/campaign-audience/removed', input),
    getMetaLimit: () => get('/meta-limit'),
    // the mobile goes in the body only, never in a URL
    sendTestWhatsApp: (input) => post('/whatsapp/test', input),
    saveCanvasAsWorkflow: (input) => post('/workflows/from-canvas', input),
    // Images (W2, R3): the file itself is the body; its name and size in the query (no personal data)
    listImages: () => get('/images'),
    uploadImage: async (input, file) => {
      if (!file) throw new ApiError(400, 'Pick an image')
      let res: Response
      try {
        res = await fetch(
          `${base}/images${query({ name: input.name, width: input.width, height: input.height })}`,
          {
            method: 'POST',
            headers: {
              accept: 'application/json',
              'x-csrf-token': csrfToken(),
              'content-type': file.type || 'application/octet-stream',
            },
            credentials: 'same-origin',
            body: file,
          }
        )
      } catch {
        throw new ApiError(503, 'The portal could not reach its server. Check the connection and try again.')
      }
      const data = (await res.json().catch(() => null)) as { error?: string } | null
      if (!res.ok) throw new ApiError(res.status, data?.error ?? `The server answered ${res.status}.`)
      return data as never
    },
    deleteImage: (imageId) => post(`/images/${id(imageId)}/delete`),
    // classification, lists, batches, groups (C-ALL part 4)
    listClassificationRuns: (workflowId, page) =>
      get(`/workflows/${id(workflowId)}/classification-runs${query({ page })}`),
    listLists: () => get('/lists'),
    uploadList: (input) => post('/lists', input),
    listBatches: () => get('/batches'),
    getBatch: (batchId, page) => get(`/batches/${id(batchId)}${query({ page })}`),
    createBatch: (input) => post('/batches', input),
    listGroups: () => get('/groups'),
    createGroup: (input) => post('/groups', input),
    countGroup: (rule) => post('/groups/count', rule),
  }

  return new Proxy(built as CampaignApi, {
    get(target, method) {
      if (method === 'demo' || method === 'then') return undefined
      const own = (target as unknown as Record<string | symbol, unknown>)[method]
      if (own) return own
      return () =>
        Promise.reject(new ApiError(501, `${String(method)} is not built on the server yet (C-B2 / C-B3).`))
    },
  })
}
